import { Box, Typography } from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import { ProductDetail, ProductStatus } from "../../types/productPlane";
import EntityLink from "../Detail/EntityLink";
import LabelValueGrid from "../Detail/LabelValueGrid";
import SourceMarks from "../Detail/SourceMarks";
import { codeWords } from "../Detail/format";

interface ProductOverviewProps {
  product: ProductDetail;
}

// Whether the product has anything the overview shows
export const hasProductOverview = (product: ProductDetail): boolean =>
  Boolean(product.description) ||
  product.categories.length > 0 ||
  product.status !== null;

const Dated: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

// The one current status in one line with its dates: the status, since and
// until when. Beneath it, quieter, the wording of the article and the date
// the status holds as of. The data holds no history.
const StatusLine: React.FC<{ status: ProductStatus }> = ({ status }) => (
  <>
    {codeWords(status.status)}
    {status.since && (
      <>
        {" · since "}
        <Dated>{status.since}</Dated>
      </>
    )}
    {status.until && (
      <>
        {" · until "}
        <Dated>{status.until}</Dated>
      </>
    )}
    {(status.wording || status.as_of) && (
      <Typography
        variant="caption"
        component="div"
        color="text.secondary"
        sx={{ mt: 0.5 }}
      >
        {status.wording && `“${status.wording}”`}
        {status.wording && status.as_of && " · "}
        {status.as_of && (
          <>
            {"as of "}
            <Dated>{status.as_of}</Dated>
          </>
        )}
      </Typography>
    )}
  </>
);

/**
 * The overview of a product: its description, its class with the
 * definition of the class, and its current status.
 */
const ProductOverview: React.FC<ProductOverviewProps> = ({ product }) => (
  <LabelValueGrid
    rows={[
      { label: "Description", value: product.description, wide: true },
      {
        label: "Category",
        value:
          product.categories.length > 0 &&
          product.categories.map((entry) => (
            <Box key={entry.category.id} sx={{ mb: 0.5 }}>
              <EntityLink
                id={entry.category.id}
                type={entry.category.type}
                name={entry.category.name}
              />
              <SourceMarks sources={entry.sources} />
              {entry.definition && (
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                >
                  {entry.definition}
                </Typography>
              )}
            </Box>
          )),
      },
      {
        label: "Status",
        value: product.status && <StatusLine status={product.status} />,
      },
    ]}
  />
);

export default ProductOverview;
